import { Typography } from "@/components";
import { isAuthenticating } from "@/libraries/helper";
import { authService } from "@/services";
import { useSyncStore } from "@/stores/sync";
import LogoutIcon from "@mui/icons-material/Logout";
import MailIcon from "@mui/icons-material/Mail";
import Button from "@mui/material/Button";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { createFileRoute, redirect, useRouter } from "@tanstack/react-router";
import { useCallback } from "react";
import OneSignal from "react-onesignal";
import { toast } from "sonner";

export const Route = createFileRoute("/subscription-required")({
	beforeLoad: async () => {
		if (!isAuthenticating()) {
			throw redirect({
				to: "/",
			});
		}
	},
	component: RouteComponent,
});

function RouteComponent() {
	const navigate = Route.useNavigate();
	const router = useRouter();
	const queryClient = useQueryClient();
	const { mutateAsync: mutateAsyncLogout } = useMutation({
		mutationFn: () => authService.logout(),
		onSuccess: async () => {
			useSyncStore.persist.clearStorage();
			await OneSignal.logout();
			queryClient.clear();
			router.history.flush();
			router.invalidate();
		},
		onError: () => {
			toast.error("Failed to logout");
		},
	});
	const handleLogout = useCallback(
		(e: React.MouseEvent<HTMLButtonElement>) => {
			e.preventDefault();
			return mutateAsyncLogout();
		},
		[mutateAsyncLogout],
	);
	const handleSubscribe = useCallback(
		(e: React.MouseEvent<HTMLButtonElement>) => {
			e.preventDefault();
			return navigate({
				to: "/subscription",
				replace: true,
			});
		},
		[navigate],
	);
	return (
		<div className="w-screen h-full wrapper bg-white">
			<div className="container flex flex-col items-center justify-center h-full gap-8">
				<svg
					viewBox="0 0 200 200"
					fill="none"
					xmlns="http://www.w3.org/2000/svg"
					className="md:w-48 md:h-48 w-32 h-32"
				>
					<title>Subscription Required</title>
					<g clipPath="url(#clip0_1521_4107)">
						<path
							d="M26.4359 161.095C25.178 162.53 22.9213 162.437 21.7752 160.912C9.48139 144.56 2.19556 124.229 2.19556 102.196C2.19556 48.1797 45.984 4.39124 99.9999 4.39124C154.016 4.39124 197.804 48.1797 197.804 102.196C197.804 125.578 189.599 147.045 175.91 163.872C157.976 141.828 130.633 127.745 99.9999 127.745C70.6734 127.745 44.362 140.652 26.4359 161.095Z"
							fill="url(#paint0_linear_1521_4107)"
						/>
						<path
							d="M21.7566 160.887V55.8882C21.7566 51.4786 25.331 47.9042 29.7406 47.9042H170.26C174.669 47.9042 178.244 51.4786 178.244 55.8882V160.887C172.88 168.026 166.563 174.406 159.481 179.84C143 167.196 122.378 159.681 100 159.681C84.7039 159.681 70.2276 163.192 57.3338 169.453C45.03 175.428 30.1634 172.014 21.9175 161.102C21.864 161.03 21.8101 160.959 21.7566 160.887Z"
							fill="#36A9FA"
						/>
						<path
							d="M29.7406 47.9042H45.7087C41.2991 47.9042 37.7247 51.4786 37.7247 55.8882V177.614C31.7678 172.69 26.4041 167.073 21.7566 160.888V55.8882C21.7566 51.4786 25.331 47.9042 29.7406 47.9042Z"
							fill="#0C8EEB"
						/>
						<path
							d="M162.276 67.8642V177.614C145.36 191.598 123.661 200 100 200C76.339 200 54.64 191.598 37.7246 177.614V67.8642C37.7246 65.6594 39.5118 63.8722 41.7166 63.8722H158.283C160.488 63.8722 162.276 65.6594 162.276 67.8642Z"
							fill="#FEF3C7"
						/>
						<path
							d="M162.276 175.953C148.303 183.647 132.248 188.024 115.17 188.024C114.677 188.024 114.185 188.02 113.693 188.013C79.8943 187.513 52.8943 159.652 52.8943 125.849V63.8722H158.284C160.488 63.8722 162.276 65.6594 162.276 67.8642V175.953Z"
							fill="#FFFBEB"
						/>
						<path
							d="M57.6847 175.649C57.6847 177.853 55.8975 179.641 53.6927 179.641C51.4879 179.641 49.7007 177.853 49.7007 175.649C49.7007 173.444 51.4879 171.657 53.6927 171.657C55.8975 171.657 57.6847 173.444 57.6847 175.649ZM77.6448 171.657C75.44 171.657 73.6528 173.444 73.6528 175.649C73.6528 177.853 75.44 179.641 77.6448 179.641C79.8496 179.641 81.6368 177.853 81.6368 175.649C81.6368 173.444 79.8496 171.657 77.6448 171.657ZM65.6687 171.657C63.464 171.657 61.6767 173.444 61.6767 175.649C61.6767 177.853 63.464 179.641 65.6687 179.641C67.8735 179.641 69.6608 177.853 69.6608 175.649C69.6608 173.444 67.8735 171.657 65.6687 171.657ZM53.6927 111.776H105.589C107.794 111.776 109.581 109.989 109.581 107.784C109.581 105.58 107.794 103.792 105.589 103.792H53.6927C51.4879 103.792 49.7007 105.58 49.7007 107.784C49.7007 109.989 51.4879 111.776 53.6927 111.776ZM53.6927 143.713H89.6208C91.8256 143.713 93.6129 141.925 93.6129 139.721C93.6129 137.516 91.8256 135.728 89.6208 135.728H53.6927C51.4879 135.728 49.7007 137.516 49.7007 139.721C49.7007 141.925 51.4879 143.713 53.6927 143.713ZM53.6927 127.744H121.557C123.762 127.744 125.549 125.957 125.549 123.752C125.549 121.548 123.762 119.76 121.557 119.76H53.6927C51.4879 119.76 49.7007 121.548 49.7007 123.752C49.7007 125.957 51.4879 127.744 53.6927 127.744ZM121.557 151.697H53.6927C51.4879 151.697 49.7007 153.484 49.7007 155.689C49.7007 157.893 51.4879 159.681 53.6927 159.681H121.557C123.762 159.681 125.549 157.893 125.549 155.689C125.549 153.484 123.762 151.697 121.557 151.697Z"
							fill="#0C8EEB"
						/>
						<path
							d="M134.331 41.9161H65.6686C57.9644 41.9161 51.6965 48.184 51.6965 55.8882V75.8483C51.6965 83.5525 57.9644 89.8203 65.6686 89.8203H134.331C142.035 89.8203 148.303 83.5525 148.303 75.8483V55.8882C148.303 48.184 142.035 41.9161 134.331 41.9161ZM136.327 75.8483C136.327 76.9489 135.432 77.8443 134.331 77.8443H65.6686C64.568 77.8443 63.6726 76.9489 63.6726 75.8483V55.8882C63.6726 54.7876 64.568 53.8922 65.6686 53.8922H134.331C135.432 53.8922 136.327 54.7876 136.327 55.8882V75.8483Z"
							fill="#B9DFFE"
						/>
						<path
							d="M78.8422 89.8203H65.6686C57.9644 89.8203 51.6965 83.5525 51.6965 75.8483V55.8882C51.6965 48.184 57.9644 41.9161 65.6686 41.9161H78.8422C71.7752 41.9161 65.9173 47.19 64.9963 54.0088C64.2255 54.2854 63.6726 55.0235 63.6726 55.8882V75.8483C63.6726 76.7129 64.2255 77.4511 64.9963 77.7277C65.9173 84.5465 71.7752 89.8203 78.8422 89.8203Z"
							fill="#7CC5FD"
						/>
						<path
							d="M129.94 67.8643H70.06C65.6504 67.8643 62.0759 64.2898 62.0759 59.8803V39.9202C62.0759 35.5106 65.6504 31.9362 70.06 31.9362H129.94C134.35 31.9362 137.924 35.5106 137.924 39.9202V59.8803C137.924 64.2898 134.35 67.8643 129.94 67.8643Z"
							fill="#00C2E5"
						/>
						<path
							d="M129.94 31.9361H116.75C119.026 28.6491 120.359 24.6599 120.359 20.3593C120.359 9.11497 111.244 0 100 0C88.7558 0 79.6408 9.11497 79.6408 20.3593C79.6408 24.6599 80.9745 28.6491 83.2504 31.9361H70.06C65.6504 31.9361 62.0759 35.5106 62.0759 39.9202V47.9042C62.0759 52.3138 65.6504 55.8882 70.06 55.8882H129.94C134.35 55.8882 137.924 52.3138 137.924 47.9042V39.9202C137.924 35.5106 134.35 31.9361 129.94 31.9361ZM100 10.5122C105.438 10.5122 109.847 14.921 109.847 20.3593C109.847 25.7976 105.438 30.2064 100 30.2064C94.5618 30.2064 90.153 25.7976 90.153 20.3593C90.153 14.921 94.5618 10.5122 100 10.5122Z"
							fill="#00DCFE"
						/>
						<path
							d="M141.517 107.784C141.517 105.58 143.304 103.792 145.509 103.792H149.501V99.8004C149.501 97.5956 151.288 95.8083 153.493 95.8083C155.698 95.8083 157.485 97.5956 157.485 99.8004V103.792H161.477C163.682 103.792 165.469 105.58 165.469 107.784C165.469 109.989 163.682 111.776 161.477 111.776H157.485V115.768C157.485 117.973 155.698 119.76 153.493 119.76C151.288 119.76 149.501 117.973 149.501 115.768V111.776H145.509C143.304 111.776 141.517 109.989 141.517 107.784ZM161.477 135.729H157.485V131.736C157.485 129.532 155.698 127.744 153.493 127.744C151.288 127.744 149.501 129.532 149.501 131.736V135.729H145.509C143.304 135.729 141.517 137.516 141.517 139.721C141.517 141.925 143.304 143.713 145.509 143.713H149.501V147.705C149.501 149.909 151.288 151.697 153.493 151.697C155.698 151.697 157.485 149.909 157.485 147.705V143.713H161.477C163.682 143.713 165.469 141.925 165.469 139.721C165.469 137.516 163.682 135.729 161.477 135.729ZM183.517 170.144C183.517 170.144 156.15 197.566 155.965 197.75C154.458 199.257 152.481 200.007 150.506 200C148.531 200.007 146.554 199.257 145.047 197.75C144.863 197.566 131.99 184.638 131.99 184.638C128.99 181.639 128.99 176.775 131.99 173.776C134.99 170.776 139.853 170.776 142.853 173.776L150.506 181.429L172.655 159.281C175.654 156.282 180.518 156.282 183.517 159.281C186.516 162.281 186.516 167.144 183.517 170.144Z"
							fill="#00DCFE"
						/>
						<path
							d="M153.493 127.744V151.697C151.288 151.697 149.501 149.909 149.501 147.705V143.713H145.509C143.304 143.713 141.517 141.925 141.517 139.721C141.517 137.516 143.304 135.729 145.509 135.729H149.501V131.736C149.501 129.532 151.288 127.744 153.493 127.744ZM149.501 99.8004V103.792H145.509C143.304 103.792 141.517 105.58 141.517 107.784C141.517 109.989 143.304 111.776 145.509 111.776H149.501V115.768C149.501 117.973 151.288 119.76 153.493 119.76V95.8083C151.288 95.8083 149.501 97.5956 149.501 99.8004ZM160.241 193.466C157.708 196.004 156.01 197.705 155.965 197.751C154.458 199.257 152.481 200.007 150.506 200C148.531 200.007 146.554 199.258 145.047 197.751C144.863 197.566 131.99 184.639 131.99 184.639C128.99 181.639 128.99 176.776 131.99 173.776C132.792 172.974 133.726 172.387 134.722 172.014C133.699 174.747 134.286 177.946 136.485 180.144C136.485 180.144 149.358 193.071 149.542 193.256C151.049 194.762 153.026 195.512 155.001 195.505C156.882 195.512 158.765 194.832 160.241 193.466Z"
							fill="#00C2E5"
						/>
					</g>
					<defs>
						<linearGradient
							id="paint0_linear_1521_4107"
							x1="197.804"
							y1="84.1317"
							x2="2.19556"
							y2="84.1317"
							gradientUnits="userSpaceOnUse"
						>
							<stop stop-color="#00DCFE" />
							<stop offset="0.725" stop-color="#0072CE" />
							<stop offset="1" stop-color="#064B86" />
						</linearGradient>
						<clipPath id="clip0_1521_4107">
							<rect width="200" height="200" fill="white" />
						</clipPath>
					</defs>
				</svg>
				<div className="flex flex-col items-center justify-center gap-4">
					<Typography fontWeight={600} fontSize={22} align="center">
						Subscription Required
					</Typography>
					<Typography fontWeight={400} fontSize={14} align="center">
						Dear colleague, thank you for using HEMI Teams. To continue using
						the app and enjoy all its features, please begin your subscription
						now.
					</Typography>
					<Typography fontWeight={400} fontSize={14} align="center">
						Without a subscription, you can no longer use the app. Please
						contact{" "}
						<a href="mailto:contact@medplanner.io" className="underline">
							contact@medplanner.io
						</a>{" "}
						for further advice.
					</Typography>
				</div>
				<div className="w-full flex flex-col items-center justify-center gap-2">
					<Button
						size="large"
						variant="contained"
						color="primary"
						fullWidth
						className="!rounded-full !h-12"
						onClick={handleSubscribe}
					>
						Subscribe Now
					</Button>
					<Button
						component="a"
						href="mailto:contact@medplanner.io"
						size="large"
						variant="outlined"
						color="primary"
						fullWidth
						className="!rounded-full !h-12 !border-zinc-300"
						startIcon={<MailIcon />}
					>
						Contact Us
					</Button>
					<Button
						size="large"
						variant="outlined"
						color="error"
						fullWidth
						className="!rounded-full !h-12 !border-zinc-300"
						startIcon={<LogoutIcon />}
						onClick={handleLogout}
					>
						Sign out
					</Button>
				</div>
			</div>
		</div>
	);
}
